/**
 * The bell in the top bar: the person's latest notifications (the same rows
 * Web Push sends to their devices, and the history migrated from legacy).
 */
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { get, post } from '../lib/api.ts';
import { relative } from '../lib/format.ts';
import { Button, Dialog, Empty, Skeleton } from './ui.tsx';

interface Item {
  id: string;
  title: string | null;
  body: string;
  url: string | null;
  readAt: string | null;
  createdAt: string;
}

export function Notifications() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ['notifications'], queryFn: () => get<{ unread: number; items: Item[] }>('/api/notifications'), refetchInterval: 60_000 });
  const read = useMutation({ mutationFn: (ids?: string[]) => post('/api/notifications/read', ids ? { ids } : {}), onSuccess: () => void qc.invalidateQueries({ queryKey: ['notifications'] }) });
  const unread = q.data?.unread ?? 0;
  const openItem = (n: Item) => {
    if (!n.readAt) read.mutate([n.id]);
    if (n.url?.startsWith('/') && !n.url.startsWith('//')) {
      setOpen(false);
      navigate({ to: n.url });
    }
  };
  return (
    <span className="notif">
      <Button variant="ghost" iconOnly icon="bell" aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'} title="Notifications" onClick={() => setOpen(true)} />
      {unread ? <span className="notif__count" aria-hidden>{unread > 99 ? '99+' : unread}</span> : null}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="Notifications"
        title={unread ? `${unread} unread` : 'All caught up'}
        width={520}
        footer={unread ? <Button variant="ghost" loading={read.isPending} onClick={() => read.mutate(undefined)}>Mark all as read</Button> : undefined}
      >
        {q.isLoading ? (
          <Skeleton h={120} />
        ) : !q.data?.items.length ? (
          <Empty title="Nothing yet.">Approvals, decisions and payslips show up here.</Empty>
        ) : (
          <ul className="list-plain notif__list">
            {q.data.items.map((n) => (
              <li key={n.id}>
                <button type="button" className="notif__item" data-unread={!n.readAt || undefined} onClick={() => openItem(n)}>
                  <span className="notif__title">{n.title ?? 'Hearth'}</span>
                  <span className="notif__body">{n.body}</span>
                  <span className="notif__time faint">{relative(n.createdAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Dialog>
    </span>
  );
}
